import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel } from '@vizit/atoms';
import { FormDialog, FormTextField } from '@vizit/forms';
import { Edit03Icon } from '@vizit/icons';
import { DialogHeaderStatus, DialogHeaderVariant } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type CollectionEditFormSchema = {
  name: string;
};

enum CollectionEditErrorType {
  None = 'none',
  Generic = 'generic',
}

const ERROR_MESSAGES: Record<CollectionEditErrorType, string> = {
  [CollectionEditErrorType.None]: '',
  [CollectionEditErrorType.Generic]: 'Error renaming collection. Please try again in a few minutes.',
};

interface CollectionEditDialogProps {
  /**
   * The ID of the collection to edit
   *
   * @type {string}
   */
  collectionId: string;
  /**
   * The current name of the collection
   *
   * @type {string}
   */
  currentName: string;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * Custom trigger button to render
   * If provided, this will be used instead of the default trigger
   *
   * @optional
   * @type {React.ReactNode}
   */
  children?: React.ReactNode;
  errorType?: CollectionEditErrorType;
  triggerButtonText?: string;
  useIcon?: boolean;
  onSubmit: (collectionId: string, data: CollectionEditFormSchema) => Promise<void>;
}

function CollectionEditDialog(props: CollectionEditDialogProps) {
  const {
    collectionId,
    currentName,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    children,
    errorType = CollectionEditErrorType.None,
    triggerButtonText = 'Edit collection',
    useIcon = false,
    onSubmit,
  } = props;

  const formSchema = yup.object().shape({
    name: yup
      .string()
      .required('Collection name is required')
      .max(200, 'Please limit collection name to under 200 characters'),
  });

  const formMethods = useForm<CollectionEditFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: currentName,
    },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError, clearErrors } = formMethods;

  // Update form when currentName changes
  React.useEffect(() => {
    reset({ name: currentName });
  }, [currentName, reset]);

  const handleOnResetForm = () => {
    clearErrors();

    reset({
      name: currentName,
    });
  };

  const handleFormSubmit = async (data: CollectionEditFormSchema) => {
    await onSubmit(collectionId, data);
  };

  React.useEffect(() => {
    const message = ERROR_MESSAGES[errorType];
    if (message) {
      setError('name', { message });
    }
  }, [errorType, setError]);

  return (
    <FormDialog
      formMethods={formMethods}
      formName="collection-edit"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      headerVariant={DialogHeaderVariant.Icon}
      headerStatus={DialogHeaderStatus.Info}
      headerIcon={Edit03Icon}
      data-testid="collection-edit-dialog"
    >
      <FormDialog.Trigger>
        {children ? (
          children
        ) : hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button level={ButtonLevel.Secondary} aria-label={triggerButtonText} data-testid="collection-edit-trigger">
            {useIcon ? <Edit03Icon size={20} /> : triggerButtonText}
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>Edit Collection</FormDialog.Title>
      <FormDialog.FormFields>
        <FormTextField
          control={control}
          name="name"
          label="Collection name"
          placeholder="Enter collection name"
          isRequired
          inputProps={{ 'data-testid': 'collection-edit-name-input' }}
        />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="collection-edit-submit">Rename</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CollectionEditDialog, CollectionEditErrorType };
export type { CollectionEditFormSchema };
